import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, getLocale, useIntl } from 'umi';
import { useInfiniteScroll } from 'ahooks';
import { buriedPointSearch } from '@/pages/home/utils/buriedPoint';
import { ReportProps } from './report';
import { segmentListMap } from './_config';
import { SegmentOption } from 'tea-component/lib/segment/SegmentOption';
import { DATABRAIN_COS_ACCELERATE, DATABRAIN_COS_DOMAIN, LANGUAGE_CN } from '@intelligence/_config/config';
import { OrderTypeMap } from '@intelligence/pages/InsightsReport/components/SortBySelector';
import { sortReportsData } from '@intelligence/pages/InsightsReport/_utils';
import { arrayRemoveElement } from '../../utils/common';
import { splitReportTagStr } from '@intelligence/v3/utils/topcharts';
import { getReportLanguages, normalizeLanguage, collectLanguageTabs, LANG_TAB_I18N_MAP } from '@intelligence/utils/reportLanguage';
import { buildReportAuthors } from '@intelligence/components/ReportCard/authors';

interface Result {
  list: [];
  nextId: number | undefined;
}
const transformAuthorAvatar = (avatar: string): string => avatar.replace(DATABRAIN_COS_DOMAIN, DATABRAIN_COS_ACCELERATE);

export default (props: ReportProps) => {
  const { getReportFun, searchType, pageLogMes, reportType } = props;

  const [dataLoading, setLoading] = useState(true);
  const locale = getLocale();
  const [dataFull, setDataFull] = useState([]);
  const [activeId, setActiveId] = useState('all');
  const [dataPartial, setDataPartial] = useState([]);
  const [dataShow, setDataShow] = useState([]);

  const [cacheRecentSearch, setCacheRecentSearch] = useState([]); // 新增
  // 排序选项
  const [orderType, setOrderType] = useState(OrderTypeMap.time);

  /** 搜索关键词 - undefined 为默认值，不搜索，搜索后清空时值为空字符; 修复回退清空搜索时没有还原列表的 bug */
  const [search, setSearch] = useState<string | undefined>(undefined);
  const location = useLocation();
  const shareBack = location?.query?.id || '';

  // 暂时
  const { formatMessage } = useIntl();
  const [currentTabSegmentData, setCurrentTabSegmentData] = useState([]);
  const [activeSegment, setActiveSegment] = useState<string>();
  const [languageSegmentValue, setLanguageSegmentValue] = useState<string>('all');



  const lazyScrollRef = useRef<HTMLDivElement>(null);
  const isInsightsCenterLibrary = reportType === 'insightsCenterLibrary';

  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const segmentList = useMemo(() => {
    return segmentListMap[activeId]?.map((item: SegmentOption) => {
      return { ...item, text: formatMessage({ id: item.text }) };
    });
  }, [activeId, locale]);

  useEffect(() => {
    setActiveSegment(segmentList?.[0].value);
    setLanguageSegmentValue('all');
  }, [activeId]);

  const languageSegmentList = useMemo(() => {
    const allTab = [{ text: formatMessage({ id: 'dbi_reports_all' }), value: 'all' }];
    if (!currentTabSegmentData?.length) return allTab;
    return [...allTab, ...collectLanguageTabs(currentTabSegmentData, 'filePosition', (lang) => {
      const key = LANG_TAB_I18N_MAP[lang];
      return key ? formatMessage({ id: key }) : `${lang} Report`;
    })];
  }, [currentTabSegmentData, locale]);

  useEffect(() => {
    if (!currentTabSegmentData?.length) {
      setDataShow([]);
      setDataPartial([]);
      return;
    }
    let filtered = currentTabSegmentData;
    if (languageSegmentValue && languageSegmentValue !== 'all') {
      const normalizedSegment = normalizeLanguage(languageSegmentValue);
      filtered = currentTabSegmentData.filter((item: any) => {
        const { fileCnLang, fileEnLang } = getReportLanguages(item);
        return (
          (normalizeLanguage(fileCnLang) === normalizedSegment && item?.pdf !== '') ||
          (normalizeLanguage(fileEnLang) === normalizedSegment && item?.pdf_en !== '')
        );
      });
    }
    setDataShow(filtered);
    setDataPartial(filtered);
  }, [languageSegmentValue, currentTabSegmentData]);

  const [tmpSearch, setTmpSearch] = useState('')
  // 排序数据
  const sortedDataPartial = useMemo(() => {
    if(search && tmpSearch !== search) {
      setTmpSearch(search);
      return dataPartial
    };
    return sortReportsData(dataPartial, orderType);
  }, [dataPartial, orderType]);

  const getReportMobileList = async () => {
    setLoading(true);
    try {
      const res = await getReportFun?.();
      const tmp = [];
      const tmpSegment = [];
      const dataListNew = [];
      const dataListTop = [];
      let tmpArrNew = [];
      const tmpSegmentList = [{ id: 'all', label: 'All' }];
      if (Array.isArray(res?.resource_list) && res?.resource_list.length > 0) {
        res.resource_list.forEach((item, index) => {
          // const time = moment(item.update_time).format('YYYY-MM-DD');
          const time = moment(item.create_time).format('YYYY-MM-DD');
          const coverImg = item.cover?.replace(DATABRAIN_COS_DOMAIN, DATABRAIN_COS_ACCELERATE);
          const creator_head_img = item.creator_head_picture?.replace(
            DATABRAIN_COS_DOMAIN,
            DATABRAIN_COS_ACCELERATE,
          );
          const itemTag = splitReportTagStr(item?.tag);
          const itemTagEN = splitReportTagStr(item?.tag_en);
          const obj = {
            ...item,
            key: index,
            resource_id: item.resource_id,
            // coverImg: item.cover,
            coverImg,
            // 筛选时高亮会修改 title, 给选中文案添加标签样式
            // 避免 title 变化有影响使用，保留原有 title 信息到 originTitle
            title: [item.title, item.title_en],
            originTitle: [item.title, item.title_en],
            report_type: [item.report_type, item.report_type_en],
            summary: [item.introduction, item.introduction_en],
            tag: [itemTag, itemTagEN],
            tag_en: itemTagEN,
            time,
            source: item.source,
            authors: buildReportAuthors(item, { transformAvatar: transformAuthorAvatar }),
            report_like: item.report_like,
            update_time: item.update_time,
            create_time: item.create_time,
          };
          tmp.push(obj);
          if (tmpSegment.indexOf(item.source) == -1) {
            tmpSegment.push(item.source);
            tmpSegmentList.push({ id: item.source, label: item.source });
          }
        });
      }

      if (tmp && Array.isArray(tmp) && tmp?.length > 0) {
        const dataArr = tmp;
        // eslint-disable-next-line no-restricted-syntax
        for (const n in dataArr) {
          // if (!!moment(dataArr[n].update_time).isAfter(moment().subtract(1, 'month'))) {
          if (!!moment(dataArr[n].create_time).isAfter(moment().subtract(1, 'month'))) {
            dataListNew.push(dataArr[n]);
          } else {
            dataListTop.push(dataArr[n]);
          }
        }
        // dataListNew.sort((a, b) => moment(b.update_time) - moment(a.update_time));
        dataListNew.sort(
          (a, b) => moment(b.create_time).valueOf() - moment(a.create_time).valueOf(),
        );
        tmpArrNew = [...dataListNew, ...dataListTop];
      } else {
        tmpArrNew = [];
      }

      // console.log(tmp)
      // console.log(tmpSegmentList)
      //  setSegmentList(tmpSegmentList)
      setDataFull(tmpArrNew);
      setDataShow(tmpArrNew);
      setDataPartial(tmpArrNew);
      setCurrentTabSegmentData(tmpArrNew);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  };

  const rules: any = {
    title: 1000,
    tag: 800,
    summary: 600,
    report_like: 600,
    report_type: 600,
    creator_name: 400,
    creator_department: 200,
    create_time: 1,
  };

  const onSearchRes = (word: string) => {
    let newData: any[] = [];
    if (typeof word === 'string' && word === '') {
      newData = dataShow;
    } else {
      dataShow.forEach((item: any, idx) => {
        let obj: any;
        item.orderNum = 0;
        const orderArr = [
          'summary',
          'creator_department',
          'tag',
          'creator_name',
          'title',
          'report_like',
          'report_type',
          'time',
        ];
        orderArr.map((orderItem: string) => {
          if (
            orderItem === 'summary' ||
            orderItem === 'creator_name' ||
            orderItem === 'creator_department' ||
            orderItem === 'title' ||
            orderItem === 'report_type' ||
            orderItem === 'time'
          ) {
            let tdata: any = item[orderItem][lang];
            if (orderItem === 'time') tdata = item[orderItem];
            // if (Array.isArray(tdata)) tdata = tdata[lang];
            if (tdata.toUpperCase().indexOf(word.toUpperCase()) != -1) {
              const itemUpper = tdata.toUpperCase();
              const replaceWord = tdata.substr(itemUpper.indexOf(word.toUpperCase()), word.length);
              const replaceHtml = `<span style="color:#326ff0">${replaceWord}</span>`;
              const regExp = new RegExp(replaceWord, 'gi');
              const newTdata = tdata.replace(regExp, replaceHtml);

              const matches = tdata.match(regExp);
              const orderNum = (item.orderNum =
                item.orderNum + rules[orderItem] + matches.length * 1000);

              if (orderItem !== 'time') {
                let tDataList = [];
                if (lang == 0) {
                  tDataList = [newTdata, item[orderItem][1]];
                } else {
                  tDataList = [item[orderItem][0], newTdata];
                }
                if (obj) {
                  obj = { ...obj, [orderItem]: tDataList, orderNum };
                } else {
                  obj = { ...item, [orderItem]: tDataList, key: idx, orderNum };
                }
              } else {
                if (obj) {
                  obj = { ...obj, [orderItem]: newTdata, orderNum };
                } else {
                  obj = { ...item, [orderItem]: newTdata, key: idx, orderNum };
                }
              }
            }
          } else if (orderItem === 'tag') {
            const tag = item.tag[lang];
            if (tag.toString().toUpperCase().indexOf(word.toUpperCase()) != -1) {
              const newTag = tag.map((s: string) => {
                const itemUpper = s.toUpperCase();
                if (itemUpper.indexOf(word.toUpperCase()) === -1) return s;
                const replaceWord = s.substr(itemUpper.indexOf(word.toUpperCase()), word.length);
                const replaceHtml = `<span style="color:#326ff0">${replaceWord}</span>`;
                const regExp = new RegExp(replaceWord, 'gi');
                const newS = s.replace(regExp, replaceHtml);

                const matches = s.match(regExp);
                item.orderNum += matches?.length * 1000;
                return newS;
              });
              const orderNum = (item.orderNum += rules.tag);
              let taLgList = [];
              if (lang == 0) {
                taLgList = [newTag, item.tag[1]];
              } else {
                taLgList = [item.tag[0], newTag];
              }
              if (obj) {
                obj = { ...obj, tag: taLgList, orderNum };
              } else {
                obj = { ...item, tag: taLgList, key: idx, orderNum };
              }
            }
          } else if (orderItem === 'report_like') {
            const orderNum = (item.orderNum = item.orderNum + rules[orderItem] + item[orderItem]);
            if (obj) {
              obj = { ...obj, orderNum };
            }
          }
        });
        if (obj) {
          newData.push(obj);
        }
        // const title = item.title[lang];
        // if (title.toUpperCase().indexOf(word.toUpperCase()) !== -1) {
        //   const itemUpper = title.toUpperCase();
        //   const replaceWord = title.substr(itemUpper.indexOf(word.toUpperCase()), word.length);
        //   const replaceHtml = `<span style="color:#326ff0">${replaceWord}</span>`;
        //   const regExp = new RegExp(replaceWord, 'gi');
        //   const newTitle = title.replace(regExp, replaceHtml);

        //   let titleList = [];
        //   if (lang === 0) {
        //     titleList = [newTitle, item.title[1]];
        //   } else {
        //     titleList = [item.title[0], newTitle];
        //   }

        //   const obj = { ...item, title: titleList, key: idx };

        //   newData.push(obj);
        // }
      });
    }
    newData = newData.sort((a: any, b: any) => {
      if (b.orderNum === a.orderNum) {
        return new Date(b.time).valueOf() - new Date(a.time).valueOf();
      }
      return b.orderNum - a.orderNum;
    });
    setDataPartial(JSON.parse(JSON.stringify(newData)));
    // setDataPartial(newData);
  };

  const debounceSearch = (word: string) => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      onSearchRes(word);
      if (pageLogMes?.pageId) {
        buriedPointSearch({ extInfo: pageLogMes?.pageId, typeId: pageLogMes?.typeId }, word);
      }
    }, 500);
  };

  const onSearch = (word: string) => {
    // setSearch(word);
    // debounceSearch(word);
    if (cacheRecentSearch.indexOf(word) == -1 && word != '') {
      const c = [...cacheRecentSearch];
      c.push(word);
      setCacheRecentSearch(c);
      localStorage.setItem(searchType, JSON.stringify(c));
    }
    onSearchRes(word);
    if (pageLogMes?.pageId) {
      buriedPointSearch({ extInfo: pageLogMes?.pageId, typeId: pageLogMes?.typeId }, word);
    }
  };

  const getRecentSearchList = () => {
    try {
      const searchRecent = localStorage.getItem(searchType) || '[]';
      const searchRecentList = JSON.parse(searchRecent);
      const list = Array.isArray(searchRecentList) ? searchRecentList : [];
      // console.log("1", list)
      setCacheRecentSearch(list);
    } catch (error) {
      console.log(error);
    }
  };

  const onClear = () => {
    onSearchRes('');
  };

  const deleteSearchRecent = (e, index, item) => {
    // console.log('delete', item)
    const c = arrayRemoveElement(cacheRecentSearch, item);

    setCacheRecentSearch(c);
    localStorage.setItem(searchType, JSON.stringify(c));
  };

  function getLoadMoreList(nextId: number | undefined, limit: number): Promise<Result> {
    let start = 0;
    if (nextId) {
      start = sortedDataPartial.findIndex((i) => i.key === nextId);
    }
    const end = start + limit;
    const list = sortedDataPartial.slice(start, end);
    const nId = sortedDataPartial[end] ? sortedDataPartial[end].key : undefined;

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          list,
          nextId: nId,
        });
      }, 500);
    });
  }

  const { data, loading, loadingMore, noMore, mutate } = useInfiniteScroll(
    (d) =>
      // console.log(d)
      getLoadMoreList(d?.nextId, 6),
    {
      target: lazyScrollRef,
      isNoMore: (d) => d?.nextId === undefined,
      reloadDeps: [sortedDataPartial],
    },
  );

  /**
   * @description: 获得当前展示语言
   * 文案语言始终跟随系统 locale，不受语言 Tab 影响；
   * 语言 Tab 仅用于过滤报告以及决定 PDF 文件的语言版本。
   */
  const lang = useMemo(() => {
    return locale === LANGUAGE_CN ? 0 : 1;
  }, [locale]);

  const handleTabContent = (activeId: string, segmentId?: string) => {
    setSearch('');
    if (activeId === 'all') {
      setCurrentTabSegmentData(dataFull);
    } else {
      const tmpData = dataFull?.filter((item: any) => {
        if (typeof item?.source === 'string') {
          const hasSubSource = segmentId && typeof item?.sub_source === 'string' && item.sub_source;

          const activeTabId = activeId?.toLocaleUpperCase();
          const source = item?.source?.toLocaleUpperCase();
          const activeSegmentId = segmentId?.toLocaleUpperCase();
          const subSource = item?.sub_source?.toLocaleUpperCase();

          // 有子级需要父级子级都匹配的数据，
          return hasSubSource
            ? activeTabId === source && activeSegmentId === subSource
            : activeTabId === source;
        }
        return false;
      });
      setCurrentTabSegmentData(tmpData);
    }
  };

  const handleSearchRecentClick = (item) => {
    setSearch(item);
    onSearchRes(item);
  };

  useEffect(() => {
    getRecentSearchList();
    getReportMobileList();
  }, []);

  return {
    activeId,
    setActiveId,
    segmentList,
    activeSegment,
    setActiveSegment,
    handleTabContent,
    onSearch,
    handleSearchRecentClick,
    deleteSearchRecent,
    cacheRecentSearch,
    dataLoading,
    dataPartial,
    currentTabSegmentData,
    lang,
    search,
    onClear,
    shareBack,
    lazyScrollRef,
    data,
    loading,
    loadingMore,
    noMore,
    mutate,
    orderType,
    setOrderType,
    languageSegmentList,
    languageSegmentValue,
    setLanguageSegmentValue,
  };
};
